<script setup lang="ts">
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import { ref, onMounted, h } from 'vue';

import { useDialog, NTooltip, NSpace, NButton } from 'naive-ui';
import LogDetailsModal from '@/components/settings/LogDetailsModal.vue';
import {
  searchIntakeAgentLogAdmin,
  getIntakeAgentLogDetails,
  restartIntakeAgentLogAdmin,
  type IntakeAgentLogInDb,
  type IntakeAgentLogInDbSlim,
  type SearchQuery,
} from '@/api';
import { logger } from '@/utils/logger';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';

const message = useGaiaToast();
const dialog = useDialog();

// Pagination
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);
const totalLogs = ref<number>(0);

// Table data
const loading = ref<boolean>(false);
const logs = ref<IntakeAgentLogInDbSlim[]>([]);
const fullLogsData = ref<Map<string, IntakeAgentLogInDb>>(new Map());
const fetchingDetails = ref<boolean>(false);

// Modal ref with type that includes the exposed methods
const logDetailsModalRef = ref<{ show: () => void; hide: () => void } | null>(null);
const selectedLog = ref<IntakeAgentLogInDb | null>(null);

// Load logs on mount and when pagination changes
onMounted(() => {
  void fetchLogs();
});

// Rate limiting for fetching details
async function fetchLogDetailsWithRateLimit(logUids: string[]): Promise<void> {
  fetchingDetails.value = true;

  // Fetch all details concurrently
  const promises = logUids.map(async uid => {
    try {
      const response = await getIntakeAgentLogDetails({ path: { intake_agent_log_uid: uid } });

      if (response.data) {
        fullLogsData.value.set(uid, response.data);
      }
    } catch (error) {
      logger.error(`Failed to fetch details for ${uid}:`, error);
    }
  });

  await Promise.all(promises);

  fetchingDetails.value = false;
}

async function fetchLogs(): Promise<void> {
  loading.value = true;

  try {
    // Create search query with proper pagination parameters
    const query: SearchQuery = {
      text: '',
      limit: pageSize.value,
      offset: (currentPage.value - 1) * pageSize.value,
      sort_key: 'created_at',
      sort_direction: 'desc',
    };

    const response = await searchIntakeAgentLogAdmin({ body: query });

    const result = response.data;
    // Handle the response using the correct structure
    if (result && typeof result === 'object') {
      if ('results' in result && Array.isArray(result.results)) {
        logs.value = result.results;
        totalLogs.value = result.result_count;

        // Fetch details for all logs with rate limiting
        const logUids = result.results.map(log => log.uid);
        await fetchLogDetailsWithRateLimit(logUids);
      } else {
        logs.value = [];
        totalLogs.value = 0;
      }
    } else {
      logs.value = [];
      totalLogs.value = 0;
    }
  } catch (error) {
    message.error('Failed to load intake agent logs');
    logger.error('Error fetching intake agent logs:', error);
    logs.value = [];
    totalLogs.value = 0;
  } finally {
    loading.value = false;
  }
}

async function handlePageChange(page: number): Promise<void> {
  currentPage.value = page;
  await fetchLogs();
}

async function handlePageSizeChange(size: number): Promise<void> {
  pageSize.value = size;
  currentPage.value = 1; // Reset to first page when changing page size
  await fetchLogs();
}

async function viewLogDetails(logUid: string): Promise<void> {
  try {
    const response = await getIntakeAgentLogDetails({ path: { intake_agent_log_uid: logUid } });

    if (response.data) {
      selectedLog.value = response.data;
      if (logDetailsModalRef.value) {
        logDetailsModalRef.value.show();
      }
    }
  } catch (error) {
    message.error('Failed to load log details');
    logger.error('Error fetching log details:', error);
  }
}

function restartIntakeAgentLog(logUid: string): void {
  dialog.warning({
    title: 'Restart Intake Agent Log',
    content:
      'This will completely run the automation again. This action cannot be undone. Are you sure you want to proceed?',
    positiveText: 'Yes, Restart',
    negativeText: 'Cancel',
    onPositiveClick: async () => {
      try {
        await restartIntakeAgentLogAdmin({
          path: { intake_agent_log_uid: logUid },
        });

        message.success('Intake agent log restarted successfully');
        // Refresh the logs table
        await fetchLogs();
      } catch (error) {
        message.error('Failed to restart intake agent log');
        logger.error('Error restarting intake agent log:', error);
      }
    },
  });
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString();
}

function getAttachmentExtensions(log: IntakeAgentLogInDbSlim): string {
  // Get the full log data with json_data
  const fullLog = fullLogsData.value.get(log.uid);

  if (!fullLog?.json_data?.Attachments || !Array.isArray(fullLog.json_data.Attachments)) {
    return 'None';
  }

  const attachments = fullLog.json_data.Attachments as { Name?: string }[];
  const extensions = attachments
    .map(attachment => {
      if (!attachment.Name) return '';
      const lastDotIndex = attachment.Name.lastIndexOf('.');
      return lastDotIndex === -1 ? '' : attachment.Name.substring(lastDotIndex + 1).toLowerCase();
    })
    .filter((ext: string) => ext.length > 0);

  return extensions.length > 0 ? extensions.join(', ') : 'None';
}
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.adminArea }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.adminIntakeAgentLogs }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="18" :wrap="false">
      <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
        {{ navLabels.adminIntakeAgentLogs }}
      </gaia-header>

      <n-data-table
        :loading="loading"
        :columns="[
          {
            title: 'From',
            key: 'from_email',
            sorter: 'default',
          },
          {
            title: 'Status',
            key: 'status',
            sorter: 'default',
            render: (row: any) => {
              return h(
                NTooltip,
                {
                  trigger: 'hover',
                },
                {
                  trigger: () => h('span', row.status),
                  default: () => row.status_message || 'No status message available',
                }
              );
            },
          },
          {
            title: 'Attachments',
            key: 'attachments',
            render: (row: any) => {
              if (fetchingDetails) {
                return h('span', { style: 'color: #999;' }, 'Loading...');
              }
              return h('span', getAttachmentExtensions(row));
            },
          },
          {
            title: 'Created At',
            key: 'created_at',
            sorter: 'default',
            render: (row: any) => formatDate(row.created_at),
          },
          {
            title: 'Updated At',
            key: 'updated_at',
            sorter: 'default',
            render: (row: any) => (row.updated_at ? formatDate(row.updated_at) : 'N/A'),
          },
          {
            title: 'Actions',
            key: 'actions',
            render: (row: any) => {
              return h(
                NSpace,
                {
                  size: 'small',
                },
                [
                  h(
                    NButton,
                    {
                      size: 'small',
                      onClick: () => viewLogDetails(row.uid),
                    },
                    { default: () => 'View Details' }
                  ),
                  h(
                    NButton,
                    {
                      size: 'small',
                      onClick: () => restartIntakeAgentLog(row.uid),
                    },
                    { default: () => 'Restart' }
                  ),
                ]
              );
            },
          },
        ]"
        :data="logs"
        :pagination="false"
      />

      <div v-if="logs.length > 0" class="pagination-container">
        <gaia-pagination
          :page="currentPage"
          :page-size="pageSize"
          :total="totalLogs"
          :page-sizes="[10, 20]"
          @update:page="handlePageChange"
          @update:page-size="handlePageSizeChange"
        />
      </div>
    </n-flex>

    <LogDetailsModal ref="logDetailsModalRef" :log-details="selectedLog" />
  </Layout>
</template>

<style scoped>
.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
</style>
